/**
 * @module Redirect
 */

import {ClientApplication} from '../../../client';
import {actionWrapper, forEachInEnum, ActionSet} from '../../helper';
import {ComplexDispatch, Group, MetaAction} from '../../types';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
  Message as ErrorMessage,
} from '../../Error';
import {isValidAbsolutePath, isValidRelativePath} from '../helper';
import {
  Action,
  ActionType,
  AdminPayload,
  CreateResource,
  LocalPayload,
  ProductVariantResource,
  RemotePayload,
  ResourceInfo,
  ResourceType,
  Section,
} from './types';

export const GROUP = Group.Navigation;

export interface ActionBase extends MetaAction {
  readonly group: typeof GROUP;
}

export interface AdminPathAction extends ActionBase {
  readonly type: typeof ActionType.ADMIN_PATH;
  readonly payload: LocalPayload;
}

export interface AdminSectionAction extends ActionBase {
  readonly type: typeof ActionType.ADMIN_SECTION;
  readonly payload: AdminPayload;
}

export interface AppAction extends ActionBase {
  readonly type: typeof ActionType.APP;
  readonly payload: LocalPayload;
}

export interface RemoteAction extends ActionBase {
  readonly type: typeof ActionType.REMOTE;
  readonly payload: RemotePayload;
}

export type RedirectAction =
  | AdminPathAction
  | RemoteAction
  | AppAction
  | AdminSectionAction
  | MetaAction;

export function isResourcePayload(resource: ResourceInfo | object): resource is ResourceInfo {
  // tslint:disable-next-line:no-boolean-literal-compare
  return typeof (resource as ResourceInfo).id === 'string';
}

export function isCreateResourcePayload(
  resource: CreateResource | object,
): resource is CreateResource {
  // tslint:disable-next-line:no-boolean-literal-compare
  return (resource as CreateResource).create === true;
}

export function isProductVariantResourcePayload(
  resource: ProductVariantResource | object,
): resource is ProductVariantResource {
  const castResource = resource as ProductVariantResource;

  // tslint:disable-next-line:no-boolean-literal-compare
  return castResource.id !== undefined && castResource.variant !== undefined;
}

export function isProductVariantCreateResourcePayload(
  resource: ProductVariantResource | object,
): resource is ProductVariantResource & {variant: CreateResource} {
  if (!isProductVariantResourcePayload(resource)) {
    return false;
  }

  return isCreateResourcePayload(resource.variant);
}

export function toAdminPath(payload: LocalPayload): AdminPathAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.ADMIN_PATH,
  });
}

export function toAdminSection(payload: AdminPayload): AdminSectionAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.ADMIN_SECTION,
  });
}

export function toRemote(payload: RemotePayload): RemoteAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.REMOTE,
  });
}

export function toApp(payload: LocalPayload): AppAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.APP,
  });
}

export function isValidAdminResource(section?: Section) {
  if (section === undefined) {
    return false;
  }
  const {name, resource} = section;
  if (!name) {
    return false;
  }

  let valid = false;
  forEachInEnum(ResourceType, resourceName => {
    if (name === resourceName) {
      valid = true;
    }
  });

  if (!valid) {
    return false;
  }

  return (
    resource === undefined ||
    isProductVariantResourcePayload(resource) ||
    isProductVariantCreateResourcePayload(resource) ||
    isCreateResourcePayload(resource) ||
    isResourcePayload(resource)
  );
}

export function validationError(action: RedirectAction): undefined | ErrorAction {
  let payload;
  switch (action.type) {
    case ActionType.ADMIN_PATH:
      payload = (action as AdminPathAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      return isValidRelativePath(payload.path)
        ? undefined
        : invalidPayload(action, 'Path is invalid. Expecting relative path');

    case ActionType.REMOTE:
      payload = (action as RemoteAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      return isValidAbsolutePath(payload.url)
        ? undefined
        : invalidPayload(action, 'Url is invalid. Expecting absolute url');

    case ActionType.APP:
      payload = (action as AppAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      return isValidRelativePath(payload.path)
        ? undefined
        : invalidPayload(action, 'Path is invalid. Expecting relative path');

    case ActionType.ADMIN_SECTION:
      payload = (action as AdminSectionAction).payload;
      if (payload === undefined) {
        return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
      }

      return isValidAdminResource(payload.section)
        ? undefined
        : invalidPayload(action, 'Invalid admin section resource');

    default:
      return invalidActionType(action);
  }
}

export class Redirect extends ActionSet implements ComplexDispatch<Section | string> {
  constructor(app: ClientApplication<any>) {
    super(app, 'Redirect', Group.Navigation);
  }

  get payload() {
    return {id: this.id};
  }

  dispatch(action: Action.ADMIN_SECTION, payload: Section): ActionSet;

  dispatch(action: Action.APP | Action.ADMIN_PATH | Action.REMOTE, payload: string): ActionSet;

  dispatch(action: Action, payload: Section | string) {
    switch (action) {
      case Action.ADMIN_PATH:
        this.app.dispatch(toAdminPath({...this.payload, path: payload as string}));
        break;

      case Action.ADMIN_SECTION:
        this.app.dispatch(toAdminSection({...this.payload, section: payload as Section}));
        break;

      case Action.APP:
        this.app.dispatch(toApp({...this.payload, path: payload as string}));
        break;

      case Action.REMOTE:
        this.app.dispatch(toRemote({...this.payload, url: payload as string}));
        break;

      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            type: action,
          }),
        );
    }

    return this;
  }
}

export function create(app: ClientApplication<any>) {
  return new Redirect(app);
}



// WEBPACK FOOTER //
// ./src/actions/Navigation/Redirect/actions.ts